import React from 'react';
import { Skeleton, SkeletonLine, SkeletonCircle, SkeletonButton } from './Skeleton';

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showActions?: boolean;
  showAvatar?: boolean;
  className?: string;
}

// Table Row Skeleton
interface TableRowSkeletonProps {
  columns: number;
  showActions?: boolean;
  showAvatar?: boolean;
}

const TableRowSkeleton: React.FC<TableRowSkeletonProps> = ({
  columns,
  showActions = true,
  showAvatar = false
}) => (
  <tr className="border-b border-gray-100">
    {showAvatar && (
      <td className="px-6 py-4">
        <SkeletonCircle size={40} />
      </td>
    )}
    {Array.from({ length: columns - (showAvatar ? 1 : 0) - (showActions ? 1 : 0) }).map((_, index) => (
      <td key={index} className="px-6 py-4">
        <SkeletonLine width={`${Math.floor(Math.random() * 40) + 60}%`} />
      </td>
    ))}
    {showActions && (
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <SkeletonButton width="60px" size="sm" />
          <SkeletonButton width="60px" size="sm" />
        </div>
      </td>
    )}
  </tr>
);

// Table Header Skeleton
interface TableHeaderSkeletonProps {
  columns: number;
  showActions?: boolean;
  showAvatar?: boolean;
}

const TableHeaderSkeleton: React.FC<TableHeaderSkeletonProps> = ({
  columns,
  showActions = true,
  showAvatar = false
}) => (
  <thead className="bg-gray-50">
    <tr>
      {showAvatar && (
        <th className="px-6 py-3">
          <SkeletonLine width="60px" />
        </th>
      )}
      {Array.from({ length: columns - (showAvatar ? 1 : 0) - (showActions ? 1 : 0) }).map((_, index) => (
        <th key={index} className="px-6 py-3 text-left">
          <SkeletonLine width={`${Math.floor(Math.random() * 30) + 70}%`} />
        </th>
      ))}
      {showActions && (
        <th className="px-6 py-3 text-left">
          <SkeletonLine width="60px" />
        </th>
      )}
    </tr>
  </thead>
);

// Main Table Skeleton
export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 8,
  columns = 6,
  showActions = true,
  showAvatar = false,
  className = ''
}) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden ${className}`}>
    {/* Table Header with Search and Filters */}
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <div>
          <SkeletonLine width="200px" className="h-6 mb-2" />
          <SkeletonLine width="150px" />
        </div>
        <div className="flex items-center space-x-3">
          <SkeletonButton width="100px" />
          <SkeletonButton width="120px" />
        </div>
      </div>
      
      {/* Search and Filter Bar */}
      <div className="flex items-center space-x-4">
        <Skeleton className="h-10 flex-1" rounded="md" />
        <SkeletonButton width="80px" />
        <SkeletonButton width="100px" />
      </div>
    </div>

    {/* Table */}
    <div className="overflow-x-auto">
      <table className="w-full">
        <TableHeaderSkeleton 
          columns={columns} 
          showActions={showActions} 
          showAvatar={showAvatar} 
        />
        <tbody className="divide-y divide-gray-100">
          {Array.from({ length: rows }).map((_, index) => (
            <TableRowSkeleton
              key={index}
              columns={columns}
              showActions={showActions}
              showAvatar={showAvatar}
            />
          ))}
        </tbody>
      </table>
    </div>

    {/* Pagination */}
    <div className="px-6 py-4 border-t border-gray-100">
      <div className="flex items-center justify-between">
        <SkeletonLine width="120px" />
        <div className="flex items-center space-x-2">
          <SkeletonButton width="80px" size="sm" />
          <div className="flex space-x-1">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="w-8 h-8" rounded="md" />
            ))}
          </div>
          <SkeletonButton width="80px" size="sm" />
        </div>
      </div>
    </div>
  </div>
);

// Simple List Skeleton
interface ListSkeletonProps {
  items?: number;
  showIcon?: boolean;
  className?: string;
}

export const ListSkeleton: React.FC<ListSkeletonProps> = ({
  items = 6,
  showIcon = true,
  className = ''
}) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`}>
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <SkeletonLine width="150px" className="h-6" />
        <SkeletonButton width="100px" size="sm" />
      </div>
      
      <div className="space-y-4">
        {Array.from({ length: items }).map((_, index) => (
          <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            {showIcon && <SkeletonCircle size={48} />}
            <div className="flex-1">
              <SkeletonLine width="70%" className="mb-2" />
              <SkeletonLine width="50%" />
            </div>
            <SkeletonButton width="80px" size="sm" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Grid Skeleton for card-based layouts
interface GridSkeletonProps {
  items?: number;
  columns?: number;
  className?: string;
}

export const GridSkeleton: React.FC<GridSkeletonProps> = ({
  items = 12,
  columns = 3,
  className = ''
}) => {
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5',
    6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-6'
  };

  return (
    <div className={`grid gap-6 ${gridClasses[columns as keyof typeof gridClasses]} ${className}`}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <SkeletonCircle size={48} className="mb-4" />
          <SkeletonLine width="80%" className="mb-2" />
          <SkeletonLine width="60%" className="mb-4" />
          <div className="space-y-2">
            <SkeletonLine width="90%" />
            <SkeletonLine width="70%" />
          </div>
          <div className="flex items-center justify-between mt-4">
            <SkeletonLine width="50%" />
            <SkeletonButton width="80px" size="sm" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default TableSkeleton;